import {
  FrameShape,
  OrderConfigResponse,
  pdSchema,
  StockSettings,
} from '@ca/common'
import {
  FaceMeasurements,
  PackageConfig,
  PackageVariant,
  RegionConfig,
} from '@ca/common/types'
import { StockItemType, StockSettingsKey, StockUtils } from '@frame/StockUtils'
import {
  Bundle,
  BundleType,
  DiscountOption,
  OrderItem,
  PromotionConfig,
  SelectedPackage,
  StepMode,
  StepsState,
} from '@order/orderTypes'
import { VariableValues } from '@order/utils/OrderVariables'
import { SessionDealContext } from 'hooks/useSession'
import { OrderResultActions } from '../../orderTypes'
import { DiscountStateActions } from './orderDiscountsSlice'
import { OrderItemsActions } from './orderItemsSlice'
import {
  OrderRequirementsActions,
  OrderRequirementsState,
} from './orderRequirementsSlice'

export type PdOverride = {
  l_pd: number
  r_pd: number
}

export type PdOverrideValues = {
  l_pd?: number | string
  r_pd?: number | string
}

export type DiscountState = {
  discounts: DiscountOption[]
}

export const PdOptions = {
  prescription: 'prescription',
  manual: 'manual',
  latestOrder: 'latestOrder',
  measurement: 'measurement',
} as const

export type PdOption = (typeof PdOptions)[keyof typeof PdOptions]

export type PdState = {
  values: Partial<Record<PdOption, PdOverrideValues | null>>
  current: PdOption | null
}

export const resolvePd = (pd: PdState) => {
  if (!pd.current) return null
  const values = pd.values[pd.current] ?? {}
  const parsed = pdSchema.safeParse(values)
  if (parsed.success) {
    return parsed.data
  }
  return null
}

export type OrderState = {
  items: OrderItem[]
  pd: PdState
  bundles: Partial<Record<BundleType, Bundle>>
  prescriptionId: number | null
  promotions: PromotionConfig[]
  packageCfg: Partial<Record<BundleType, SelectedPackage>>
  stock: Record<StockSettingsKey, StockSettings>
  comment: string | null
  invoice: boolean
  activeItemId?: string
}

export const getStockState = (
  item: OrderItem,
  stock: OrderState['stock'],
  config: RegionConfig | undefined,
  type: StockItemType = 'frame'
) => {
  const itemStock = stock[StockUtils.getItemKey(item.id, type)]
  return StockUtils.getState(item, itemStock, config)
}

export const initialOrderState: OrderState = {
  items: [],
  bundles: {},
  pd: {
    values: {},
    current: null,
  },
  prescriptionId: null,
  promotions: [],
  packageCfg: {},
  stock: {},
  invoice: false,
  comment: null,
}

export type OrderActions = {
  setPrescriptionId: (prescriptionId: number) => void
  addPromotion: (promotion: PromotionConfig) => void

  setPackage: (
    item: PackageConfig,
    options: {
      variant?: PackageVariant | null
      region: RegionConfig
      frames: number
      bundle?: BundleType
      overrides: VariableValues
    }
  ) => SelectedPackage
  reset: () => void
  updatePd: (pd: PdOverrideValues | null, type: PdOption) => void

  setUseInvoice: (value: boolean) => void

  updateFrame: (
    frameId: number,
    data: {
      measurement?: FaceMeasurements
      shape?: FrameShape
    }
  ) => void
  setComment: (comment: string | null) => void

  completeItem: (
    itemId: string,
    states: Partial<Record<StepMode, StepsState>>,
    bundleType: BundleType
  ) => void
  updateItem: (
    itemId: string,
    states: Partial<Record<StepMode, StepsState>>,
    bundleType: BundleType
  ) => void
} & OrderResultActions &
  OrderItemsActions &
  OrderRequirementsActions &
  DiscountStateActions

type StepsStates = Partial<Record<StepMode, StepsState>>

export type FinalOrderState = {
  order: OrderState
  result: OrderConfigResponse | null
  steps: Record<string, StepsStates>
} & DiscountState &
  OrderRequirementsState &
  OrderActions

export type ResolvedDealsContext = Record<
  BundleType,
  SessionDealContext | undefined
>

export type SavedOrder = {
  id: string
  order: OrderState
  discounts: DiscountOption[]
  result: OrderConfigResponse | null
  deals: ResolvedDealsContext
  primaryId?: string
}
